import { useCallback, useState } from "react"
import { SectionInput } from "../pages/WorkflowFileExplorer/sections/ScopeWorkflowsSection/components/AddScopeWorkflowForm.types"
import SectionType from "../enums/sectionType"
import { MAX_PIPE_DIAMETER, MAX_PIPE_LENGTH, MAX_SCOPE_WORKFLOW_SECTIONS, MAX_SECTION_LENGTH, MAX_TEMP, MAX_THICKNESS, MIN_PIPE_DIAMETER, MIN_PIPE_LENGTH, MIN_SECTION_LENGTH, MIN_TEMP, MIN_THICKNESS } from "../utils/constants";
import { ScopeWorkflow } from "../types/types"
import { anyMoreThanOnce, floatMoreThanOnce, intMoreThanOnce, possibleNegativefloatMoreThanOnce } from "../utils/validation";

type useScopeWorkflowInputsProps = {
  locations: string[];
  insulationMaterials: string[];
  claddingMaterials: string[];
  currentSection: number;
}

const useScopeWorkflowInputs = (props: useScopeWorkflowInputsProps) => {

  const {
    locations,
    insulationMaterials,
    claddingMaterials,
    currentSection
  } = props

  const [pipeId, setPipeId] = useState<string>("")
  const [pipeIdError, setPipeIdError] = useState<Error|null>(null)

  const [pipeLength, setPipeLength] = useState<string>("")
  const [pipeLengthError, setPipeLengthError] = useState<Error|null>(null)

  const [pipeTemperature, setPipeTemperature] = useState<string>("")
  const [pipeTemperatureError, setPipeTemperatureError] = useState<Error|null>(null)

  const [pipeDiameter, setPipeDiameter] = useState<string>("")
  const [pipeDiameterError, setPipeDiameterError] = useState<Error|null>(null)

  const [assetType, setAssetType] = useState<number>(0)
  const assetTypes = ["PIPE", "VESSEL"]
  // const [assetTypeError, setAssetTypeError] = useState<Error|null>(null)

  const [workflowStart, setWorkflowStart] = useState<string>("")
  const [workflowStartError, setWorkflowStartError] = useState<Error|null>(null)

  const [workflowEnd, setWorkflowEnd] = useState<string>("")
  const [workflowEndError, setWorkflowEndError] = useState<Error|null>(null)

  const [insulationMaterial, setInsulationMaterial] = useState<number>(0)

  const [insulationThickness, setInsulationThickness] = useState<string>("")
  const [insulationThicknessError, setInsulationThicknessError] = useState<Error|null>(null)

  const [claddingMaterial, setCladdingMaterial] = useState<number>(0)

  const [claddingThickness, setCladdingThickness] = useState<string>("")
  const [claddingThicknessError, setCladdingThicknessError] = useState<Error|null>(null)

  const [location, setLocation] = useState<number>(0)

  const [sectionType, setSectionType] = useState<number[]>([])

  const [selectedTracingCableOption, setSelectedTracingCableOption] = useState<number>(1)
  const tracingCableOptions = ["YES", "NO"]

  const [sectionStarts, setSectionStarts] = useState<SectionInput[]>([])
  const [sectionEnds, setSectionEnds] = useState<SectionInput[]>([])

  const [sectionAngles, setSectionAngles] = useState<SectionInput[][]>([])
  const [newAngle, setNewAngle] = useState<string>("")
  const [newAngleError, setNewAngleError] = useState<Error|null>(null)

  const sectionTypes = Object.values(SectionType)


  const onChangeSectionStart = (value: string, error: Error|null, sectionIndex?: number) => {
    setSectionStarts(sectionStarts.map((start, i) => {

      const sectionToCheck = sectionIndex !== undefined ? sectionIndex : currentSection
      if (i === sectionToCheck) {
        return { value: value, error: error }
      }
      return start
    }))
  }

  const onChangeSectionEnd = (value: string, error: Error|null, sectionIndex?: number) => {
    setSectionEnds(sectionEnds.map((end, i) => { 

      const sectionToCheck = sectionIndex !== undefined ? sectionIndex : currentSection
      if (i === sectionToCheck) {
        return { value: value, error: error }
      }
      return end
    }))
  }

  const onChangeSectionType = (value: number) => {
    setSectionType(sectionType.map((type, i) => {
      if (i === currentSection) {
        return value
      }
      return type
    }))
  }

  const onAddAngle = (angle?: SectionInput) => {
    let angleToAdd: SectionInput|undefined = angle

    if (angle === undefined && !validateNewAngle()) {
      return
    }

    if (angleToAdd === undefined) {
      angleToAdd = {
        value: newAngle,
        error: null
      }
    }
    
    setSectionAngles(sectionAngles.map((angles, i) => {
      if (i === currentSection) {
        const sectionAnglesCopy = sectionAngles[i]
        sectionAnglesCopy.push(angleToAdd!)
        return sectionAnglesCopy
      }
      return angles
    }))
    setNewAngle("")
  }

  const onRemoveAngle = (index: number) => {
    if (sectionAngles[currentSection].length === 1) { return }
    setSectionAngles(sectionAngles.map((angles, sectionIndex) => {
      if (currentSection === sectionIndex) {
        return angles.filter((_, i) => i !== index)
      }
      return angles
    }))
  }

  const onAddSectionValues = () => {
    if (sectionStarts.length >= MAX_SCOPE_WORKFLOW_SECTIONS) { return }
    // const start = sectionStarts.length === 0 ? `${workflowStart}` : `${sectionEnds[currentSection-1].value}`
    // const end = parseInt(start) + 1000
    const start = 0
    const end = 1000

    const startInput = {
      value: `${start}`,
      error: null
    }

    const endInput = {
      value: isNaN(end) ? "" : `${end}`,
      error: null
    }

    const sectionAngleInput = {
      value: "0",
      error: null
    }

    const angles = [sectionAngleInput]

    setSectionStarts([...sectionStarts, ...[{...startInput}]])
    
    setSectionEnds([...sectionEnds, ...[{...endInput}]])
    setSectionAngles([...sectionAngles, ...[angles]])
    setSectionType([...sectionType, ...[0]])
  }

  const onRemoveCurrentSectionInputs = useCallback(() => {  
    if (sectionStarts.length === 1) { return }
    setSectionStarts(starts => starts.filter((_, i) => i !== currentSection))
    setSectionEnds(ends => ends.filter((_, i) => i !== currentSection))
    setSectionAngles(angles => angles.filter((_, i) => i !== currentSection))
    setSectionType(types => types.filter((_, i) => i !== currentSection))
  }, [sectionStarts, currentSection])

  const setScopeWorklfowForm = useCallback((scopeWorkflow: ScopeWorkflow) => {
    setPipeId(scopeWorkflow.pipe_id)
    setLocation(locations.indexOf(scopeWorkflow.location))
    setPipeLength(scopeWorkflow.pipe_length.toString())
    setPipeTemperature(scopeWorkflow.pipe_temp.toString())
    setPipeDiameter(scopeWorkflow.pipe_diameter.toString())
    setAssetType(0)
    setWorkflowStart(scopeWorkflow.start.toString())
    setWorkflowEnd(scopeWorkflow.end.toString())
    setInsulationMaterial(insulationMaterials.indexOf(scopeWorkflow.insulation_material))
    setInsulationThickness(scopeWorkflow.insulation_thickness.toString())
    setCladdingMaterial(claddingMaterials.indexOf(scopeWorkflow.cladding_material))
    setCladdingThickness(scopeWorkflow.cladding_thickness.toString())
    setSelectedTracingCableOption(scopeWorkflow.has_tracing_cable ? 0 : 1)
    setSectionType(JSON.parse(scopeWorkflow.sections).map((section: number[]) => {
      return `${section[2]}`
    }))
    let starts = []
    let ends = []
    let scopeAngles = []
    const sections = JSON.parse(scopeWorkflow.sections)
    const angles = JSON.parse(scopeWorkflow.angles)
    for (let i=0; i<sections.length; i++) {
      starts.push({ value: `${sections[i][0]}`, error: null })
      ends.push({ value: `${sections[i][1]}`, error: null })
      const sectionScopeAngles = angles[i].map((angle: number) => { return { value: `${angle}`, error: null }})
      scopeAngles.push(sectionScopeAngles)
    }
    setSectionStarts(starts)
    setSectionEnds(ends)
    setSectionAngles(scopeAngles)
  }, [locations, insulationMaterials, claddingMaterials])

  const clearInputs = useCallback(() => {
    setPipeId("test1234")
    setLocation(0)
    setPipeLength("2000")
    setPipeTemperature("78")
    setPipeDiameter("168")
    setAssetType(0)
    setWorkflowStart("0")
    setWorkflowEnd("1250")
    setInsulationMaterial(0)
    setInsulationThickness("10")
    setCladdingMaterial(0)
    setCladdingThickness("10")
    setSelectedTracingCableOption(1)
    setSectionStarts([])
    setSectionEnds([])
    setSectionAngles([])
    setSectionType([])
    setPipeIdError(null)
    setPipeLengthError(null)
    setPipeTemperatureError(null)
    setPipeDiameterError(null)
    setWorkflowStartError(null)
    setWorkflowEndError(null)
    setInsulationThicknessError(null)
    setCladdingThicknessError(null)
    setNewAngleError(null)
  }, [])

  const validatePipeId = () => {
    setPipeIdError(null)
    const isValid = anyMoreThanOnce(pipeId)
    setPipeIdError(isValid ? null : new Error("Invalid pipe id."))
    return isValid
  }

  const validatePipeLength = () => {
    setPipeLengthError(null)
    let isValid = floatMoreThanOnce(pipeLength)
    setPipeLengthError(isValid ? null : new Error("Invalid pipe length."))
    if (!isValid) { return isValid }

    const lengthValue = parseFloat(pipeLength)
    isValid = isValid && lengthValue >= MIN_PIPE_LENGTH && lengthValue < MAX_PIPE_LENGTH
    if (!isValid) {
      setPipeLengthError(new Error("Invalid pipe length."))
    }
    return isValid
  }

  const validatePipeDiameter = () => {
    setPipeDiameterError(null)
    let isValid = floatMoreThanOnce(pipeDiameter)
    setPipeDiameterError(isValid ? null : new Error("Invalid pipe diameter."))
    
    if (!isValid) { return isValid }

    const diameterValue = parseFloat(pipeDiameter)
    isValid = isValid && diameterValue >= MIN_PIPE_DIAMETER && diameterValue < MAX_PIPE_DIAMETER
    if (!isValid) {
      setPipeDiameterError(new Error("Invalid pipe diameter."))
    }
    return isValid
  }

  const validatePipeTemperature = () => {
    setPipeDiameterError(null)
    let isValid = possibleNegativefloatMoreThanOnce(pipeTemperature)
    setPipeTemperatureError(isValid ? null : new Error("Invalid pipe temperature."))
    
    if (!isValid) { return isValid }

    const tempValue = parseFloat(pipeTemperature)
    isValid = isValid && tempValue >= MIN_TEMP && tempValue < MAX_TEMP
    if (!isValid) {
      setPipeTemperatureError(new Error("Invalid pipe temperature."))
    }
    return isValid
  }

  const validateStartPoint = () => {
    setWorkflowEndError(null)
    const isValid = floatMoreThanOnce(workflowStart)
    setWorkflowStartError(isValid ? null : new Error("Invalid workflow start."))

    return isValid
  }

  const validateEndPoint = () => {
    setWorkflowEndError(null)
    const isValid = floatMoreThanOnce(workflowEnd)
    setWorkflowEndError(isValid ? null : new Error("Invalid workflow end."))
    
    return isValid
  }

  const validateStartEndPoint = () => {
    setWorkflowStartError(null)
    setWorkflowEndError(null)
    if (validateStartPoint() && validateEndPoint()) {
      if (parseInt(workflowStart) > parseInt(workflowEnd)) {
        setWorkflowStartError(new Error("Invalid start point."))
        return false
      } 
      if (parseInt(workflowEnd) - parseInt(workflowStart) < MIN_SECTION_LENGTH) {
        setWorkflowEndError(new Error("Invalid end point."))
        return false
      }

      if (parseInt(workflowEnd) - parseInt(workflowStart) > parseInt(pipeLength)) {
        setWorkflowStartError(new Error("Invalid start point."))
        setWorkflowEndError(new Error("Invalid end point."))
        return false
      }

      return true
    }
    return false
  }

  const validateCladdingThickness = () => {
    setCladdingThicknessError(null)
    let isValid = intMoreThanOnce(claddingThickness)
    setCladdingThicknessError(isValid ? null : new Error("Invalid cladding thickness."))
    

    const thicknessValue = parseFloat(claddingThickness)
    isValid = isValid && thicknessValue >= MIN_THICKNESS && thicknessValue < MAX_THICKNESS
    if (!isValid) {
      setCladdingThicknessError(new Error("Invalid cladding thickness."))
    }
    return isValid 
  }

  const validateInsulationThickness = () => {
    setInsulationThicknessError(null)
    let isValid = intMoreThanOnce(insulationThickness)
    setInsulationThicknessError(isValid ? null : new Error("Invalid insulation thickness."))
    

    const thicknessValue = parseFloat(insulationThickness)
    isValid = isValid && thicknessValue >= MIN_THICKNESS && thicknessValue < MAX_THICKNESS
    if (!isValid) {
      setInsulationThicknessError(new Error("Invalid cladding thickness."))
    }
    return isValid 
  }

  const validateSectionStart = (sectionIndex: number) => {
    const start = sectionStarts[sectionIndex].value

    if (!intMoreThanOnce(start)) {
      onChangeSectionStart(start, new Error("Invalid start"), sectionIndex)
      return false
    } 

    if (parseInt(start) < parseInt(workflowStart)) {
      onChangeSectionStart(start, new Error(`Start must be >= ${workflowStart}mm`), sectionIndex)
      return false
    }

    if (sectionIndex === 0) { return true }

    const previousEnd = sectionEnds[sectionIndex-1].value

    if (parseInt(start) < parseInt(previousEnd)) {
      onChangeSectionStart(start, new Error(`Section start must be >= ${previousEnd}mm`), sectionIndex)
      return false
    }

    if (parseInt(start) > parseInt(workflowEnd)) {
      onChangeSectionStart(start, new Error(`Section start cannot be greater than ${workflowEnd}mm`), sectionIndex)
      return false
    }

    return true
  }

  const validateSectionEnd = (sectionIndex: number) => {
    const end = sectionEnds[sectionIndex].value

    if (!intMoreThanOnce(end)) {
      onChangeSectionEnd(end, new Error("Invalid end"), sectionIndex)
      return false
    }

    if (parseInt(end) < parseInt(workflowStart)) {
      onChangeSectionEnd(end, new Error(`End must be > ${workflowStart}mm`), sectionIndex)
      return false
    }

    if (parseInt(end) > parseInt(workflowEnd)) {
      onChangeSectionEnd(end, new Error(`End must be < ${workflowEnd}mm`), sectionIndex)
      return false
    }

    return true
  }

  const validateSectionStartEnd = (sectionIndex: number) => {
    const start = sectionStarts[sectionIndex].value
    const end = sectionEnds[sectionIndex].value

    if (parseInt(start) > parseInt(end)) {
      onChangeSectionStart(start, new Error(`Start must be less than ${parseInt(end)}mm`), sectionIndex)
      return false
    }

    const scanLength = parseInt(end) - parseInt(start)
    if (scanLength > MAX_SECTION_LENGTH) {
      onChangeSectionEnd(end, new Error(`Scan can only be max ${MAX_SECTION_LENGTH}mm`), sectionIndex)
      return false
    }

    if (scanLength < MIN_SECTION_LENGTH) {
      onChangeSectionEnd(end, new Error(`Scan can only be min ${MIN_SECTION_LENGTH}mm`), sectionIndex)
      return false
    }
    return true
  }

  const validateSectionAngles = (sectionIndex: number) => {
    return sectionAngles[sectionIndex].length > 0
  }

  const validateSections = () => {
    let validSections = true
    for (let section=0; section<sectionStarts.length; section++) {
      const validStart = validateSectionStart(section)
      const validEnd = validateSectionEnd(section)
      const validStartEnd = validateSectionStartEnd(section)
      const validSectionAngle = validateSectionAngles(section)
      
      const validSection = validStart && validEnd && validStartEnd && validSectionAngle

      // if (validSections && !validSection && scrollRef && scrollRef?.current) {
      //   scrollRef.current.scrollTo({ x: (width * section)-(2*(section)*HORIZONTAL_PADDING), animated: true })
      // }

      validSections = validSections && validSection

      if (!validSections) { break }
    }
    return validSections
  }

  const validateNewAngle = () => {
    let isValid = intMoreThanOnce(newAngle)
    setNewAngleError(isValid ? null : new Error("Invalid angle."))

    if (!isValid) { 
      return false 
    }

    const angle = parseInt(newAngle)
    isValid = isValid && angle >= 0 && angle <= 360

    if (!isValid) {
      setNewAngleError(new Error("Angle must be between 0 and 360."))
      return false
    }
    return isValid
  }

  const isValidSectionForm = () => {
    return sectionStarts.length > 0 && validateSections()
  }

  const isScopeValidWorkflowInfo = () => {
    const isValidPipeId = validatePipeId()
    const isValidPipeDiameter = validatePipeDiameter()
    const isValidPipeLength   = validatePipeLength()
    const isValidPipeTemp     = validatePipeTemperature()
    const isValidStartPoint   = validateStartPoint()
    const isValidEndPoint     = validateEndPoint()
    const isValidStartEndPoint = validateStartEndPoint()
    const isValidCladdingThickness = validateCladdingThickness()
    const isValidInsulationThickness = validateInsulationThickness()

    const isValid = 
      isValidPipeId && 
      isValidPipeDiameter && 
      isValidPipeLength && 
      isValidPipeTemp && 
      isValidStartPoint &&
      isValidEndPoint && 
      isValidStartEndPoint && 
      isValidCladdingThickness &&
      isValidInsulationThickness

    return isValid
  }

  return [{
    pipeId: {value: pipeId, onChange: setPipeId, error: pipeIdError},
    location: {values: locations, onChange: setLocation, selected: location },
    pipeLength: {value: pipeLength, onChange: setPipeLength, error: pipeLengthError},
    pipeTemperature: {value: pipeTemperature, onChange: setPipeTemperature, error: pipeTemperatureError},
    pipeDiameter: {value: pipeDiameter, onChange: setPipeDiameter, error: pipeDiameterError},
    assetType: {values: assetTypes, onChange: setAssetType, selected: assetType},
    workflowStart: {value: workflowStart, onChange: setWorkflowStart, error: workflowStartError},
    workflowEnd: {value: workflowEnd, onChange: setWorkflowEnd, error: workflowEndError},
    insulationMaterial: {values: insulationMaterials, onChange: setInsulationMaterial, selected: insulationMaterial},
    insulationThickness: {value: insulationThickness, onChange: setInsulationThickness, error: insulationThicknessError},
    claddingMaterial: {values: claddingMaterials, onChange: setCladdingMaterial, selected: claddingMaterial },
    claddingThickness: {value: claddingThickness, onChange: setCladdingThickness, error: claddingThicknessError},
    hasTracingCable: {values: tracingCableOptions, onChange: setSelectedTracingCableOption, selected: selectedTracingCableOption},
    sectionStarts: { value: sectionStarts, onChange: onChangeSectionStart },
    sectionEnds: { value: sectionEnds, onChange: onChangeSectionEnd },
    sectionType: { values: sectionTypes, selected: sectionType[currentSection], onChange: onChangeSectionType},
    newAngle: { value: newAngle, onChange: setNewAngle, error: newAngleError, placeholder: "Add angle"},
    sectionAngles: sectionAngles,
    onAddAngle,
    onRemoveAngle,
    onAddSectionValues,
    setScopeWorklfowForm,
    clearInputs,
    onRemoveCurrentSectionInputs,
    validateNewAngle,
    isScopeValidWorkflowInfo,
    isValidSectionForm
  }]
}

export default useScopeWorkflowInputs